/**
 * MapWorkspaceLayers — renders an active workspace's locations as clustered
 * markers over the persistent map MapShell.
 *
 * Why a side-effect-only child
 * ----------------------------
 * Like {@link MapCamera}, this component renders `null` — it exists to own
 * a slice of map state that mounts/unmounts over the single persistent shell.
 * It is mounted by `MapSurface` only while a workspace is active, so its
 * mount boundary is exactly "a workspace is selected", which lets it:
 *
 *   1. Fetch the lightweight marker set (id + coordinates) for that workspace
 *      via `useWorkspaceLocationMarkersQuery` — one request, cached 5 min so a
 *      re-switch re-displays markers with no refetch.
 *   2. Adapt those markers to the clusterable `ILocation` shape and feed
 *      `useClusters` / `useClusterInteractions` — those hooks only resolve
 *      clicks to a location id; this component owns the actual select/
 *      deselect decision and the resulting icon/camera reaction.
 *   3. Order the cluster layers above the zones overlay
 *      and, while no card holds the camera, frame it to the marker set once
 *      per workspace id.
 *
 * Degradation
 * -----------
 * - Empty workspace → `markers: []` → the shared `setupLocationsClusters`
 *   early-returns; the map stays clean with no markers and no error.
 * - Fetch error → a single non-blocking `toast.error` and NO partial markers
 *   The map stays fully usable.
 *
 * Selection lives in the URL (survives reload; cleared on workspace switch).
 */
import { bbox } from '@turf/turf';
import type { Feature, FeatureCollection, Point } from 'geojson';
import { useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import type { MapShellRenderState } from '@/features/locations/components/MapShell';
import { useClusters } from '@/features/locations/hooks/useClusters';
import { useClusterInteractions } from '@/features/locations/hooks/useClustersInteractions';
import type { LayerVisibilityState } from '@/features/locations/hooks/useLayerVisibilityFilters';
import { useHighestPerformingCompetitorMark } from '@/features/locations/hooks/useHighestPerformingCompetitorMark';
import { useLocationDetailLayers } from '@/features/locations/hooks/useLocationDetailLayers';
import { useLinkedPlace } from '@/features/locations/hooks/useLinkedPlace';
import { useLocationResultsQuery } from '@/features/locations/services/locations.api';
import { applyMaxAadtVisibility } from '@/features/locations/utils/applyLayerVisibility';
import { keptMapPitch } from '@/features/locations/utils/basemap';
import { MapLayersService } from '@/features/locations/utils/MapLayersService';
import { radiusToZoomLevel } from '@/features/locations/utils/radiusHelpers';
import { useWorkspaceLocationMarkersQuery } from '@/features/workspaces/services/workspace.api';
import { useMixpanel } from '@/hooks/useMixpanel';
import { ACTION_TRACKED } from '@/libs/mixpanel';
import type { ILocation, ILocationMarker } from '@/types/api';
import { cameraDuration, INITIAL_FIT_DURATION_MS } from '../utils/cameraMotion';
import { WorkspaceLocationPopup } from './WorkspaceLocationPopup';

/**
 * Zoom level applied when framing a single-marker workspace (fitBounds on a
 * degenerate point is invalid, so we ease to the point at a sensible zoom).
 * Deliberately above the clustered source's `CLUSTER_MAX_ZOOM` (11) so the lone
 * pin reads as "your location" rather than sitting at the clustering boundary;
 * the two thresholds are independent.
 */
const SINGLE_MARKER_ZOOM = 14;

/** Non-blocking copy shown when the marker fetch fails. Fixed, friendly
 *  text — never the raw backend message, which could leak internal detail. */
const MARKERS_FETCH_ERROR_MESSAGE =
  "Couldn't load this workspace's locations. The map is still usable.";

/** Non-blocking copy shown when the on-demand location-detail fetch fails. Fixed,
 *  friendly text — never the raw backend/Axios message, which could leak internal
 *  detail. The map stays usable and the popup simply does not open.
 *
 *  Deliberately hedges on "may still be finishing up": a fetch for a location
 *  that's still an unresolved compute-run tempId is disabled (see
 *  `isSelectedLocationComputing` below) and should never reach this toast, but
 *  a navigate-away-and-back round trip (e.g. to /export) tears down the SSE
 *  subscription that reconciles the tempId to its real id — if that
 *  reconciliation hasn't caught up yet by the time this fires, a flat
 *  "couldn't load, try again" would be actively misleading, since retrying
 *  immediately fails the same way until the run resolves. */
const LOCATION_DETAIL_FETCH_ERROR_MESSAGE =
  "Couldn't load this location's details — it may still be finishing up. Please try again in a moment.";

interface MapWorkspaceLayersProps {
  shell: MapShellRenderState;
  /** The active workspace id. Only rendered when a workspace is active. */
  workspaceId: string;
  /**
   * Whether the camera may be framed to the marker set. `MapSurface` passes
   * false while a zone or deal card holds the camera, so the fit never fights it.
   */
  allowFit?: boolean;
  /**
   * Imperative auto-select request: when a single-pin
   * add completes, `MapSurface` raises this to the reconciled marker id so its
   * detail opens without a click. Each new non-null value selects that marker;
   * the user can still dismiss the popup afterwards.
   *
   * Dedupe behaviour: the component tracks the last consumed value in
   * `consumedAutoSelectRef` so the same id does not re-open a popup the user
   * dismissed. The ref is reset on workspace switch so a re-add on a
   * re-activated workspace can auto-select again.
   */
  autoSelectId?: string | null;
  /**
   * Retained map-session layer-visibility state to apply to each selected
   * pin's overlays. Passed down from the map surface so
   * the active filter persists across pin selections.
   */
  filterState: LayerVisibilityState;
  /** Competitor station tiles. The crown is skipped while this is off. */
  competitorTilesEnabled?: boolean;
  /** Reports pin selection up so the surface can enable/disable the filter
   *  controls. Fires true when a pin is selected, false when
   *  the selection clears (deselect or workspace-switch reset). Does NOT fire
   *  on initial mount (hasSelection starts false; only transitions are reported). */
  onSelectionChange?: (hasSelection: boolean) => void;
  /** The selected location id from `useMapSelection` (`?location=<id>`), or `null`. */
  locationId: string | null;
  /** Selects a location (writes `?location=<id>`). */
  onSelectLocation: (id: string) => void;
  /** Clears the location selection (removes `?location=`). */
  onClearLocation: () => void;
  /** Selects a deal (writes `?deal=<id>`, which displaces `?location=`). */
  onSelectDeal: (id: string) => void;
  /**
   * Selects the deal a creation just landed. Distinct from `onSelectDeal` because the caller marks
   * this one as promoted, and only a promotion is celebrated on the map.
   */
  onDealCreated?: (id: string) => void;
  /** tempIds of locations with an active compute run (disables the results query). */
  computingLocationIds?: ReadonlySet<string>;
  /** Live 0–100 percent for the active compute run, shown in the popup's skeleton. */
  computingProgress?: number | null;
  /** Project id of the selected location's active compute run, or `null` if
   *  it isn't computing (or its run hasn't registered a project id yet). Lets
   *  the popup's Cancel button target the right project. */
  computingProjectId?: string | null;
  /** Cancels the selected location's active compute run by project id. */
  onCancelComputing?: (projectId: string) => void;
}

/** Default for the optional `computingLocationIds` prop. */
const NO_COMPUTING_LOCATIONS: ReadonlySet<string> = new Set();

/**
 * Adapt a lightweight marker to the shared clusterable `ILocation` shape.
 * `radius: 0` → marker click eases to `radiusToZoomLevel(0)` (== 14, matching
 * `SINGLE_MARKER_ZOOM`); `address: ''` is unused by the cluster hooks today.
 * Give map markers real values here if either hook starts reading these.
 */
function markerToLocation(marker: ILocationMarker): ILocation {
  return {
    id: marker.id,
    coordinates: { latitude: marker.latitude, longitude: marker.longitude },
    radius: 0,
    address: '',
  };
}

/** Warn (bottom-right toast) when the selected pin shares its spot with others. */
function warnIfCoincident(count: number) {
  if (count > 1) {
    toast.warning(`${count} locations at this spot — showing the most recent.`);
  }
}

export function MapWorkspaceLayers({
  shell,
  workspaceId,
  allowFit = false,
  autoSelectId = null,
  filterState,
  competitorTilesEnabled = false,
  onSelectionChange,
  locationId: selectedLocationId,
  onSelectLocation,
  onClearLocation,
  onSelectDeal,
  onDealCreated,
  computingLocationIds = NO_COMPUTING_LOCATIONS,
  computingProgress = null,
  computingProjectId = null,
  onCancelComputing,
}: MapWorkspaceLayersProps) {
  const { map, isMapLoaded, imagesInitialized } = shell;

  const { data, isError } = useWorkspaceLocationMarkersQuery(workspaceId);

  // On a fetch error, surface a single non-blocking toast and render NO
  // markers. A flapping re-render must not re-toast, so track the handled id.
  const toastedErrorIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isError) {
      toastedErrorIdRef.current = null;
      return;
    }
    if (toastedErrorIdRef.current === workspaceId) return;
    toastedErrorIdRef.current = workspaceId;
    toast.error(MARKERS_FETCH_ERROR_MESSAGE);
  }, [isError, workspaceId]);

  // Adapt markers → clusterable locations. On error we render nothing (no
  // partial markers), so collapse to an empty set in that case.
  const locations = useMemo<ILocation[]>(() => {
    if (isError || !data) return [];
    return data.markers.map(markerToLocation);
  }, [data, isError]);

  // Exclude computing locations — MapSurface's MapComputePinMarker is the
  // sole pin shown for those, to avoid a native icon rendering alongside it.
  const clusterableLocations = useMemo(
    () => locations.filter((loc) => !computingLocationIds.has(loc.id)),
    [locations, computingLocationIds],
  );

  const { setupLocationsClusters, cleanupLocationsClusters, applyLocations, applySelection } =
    useClusters(map, clusterableLocations);
  const { setupClusterInteractions } = useClusterInteractions(map);

  // ── Report selection changes up to the parent surface ────────────────────────
  // Fires on mount too: a restored `?location=` can already be true on a cold load.
  const hasSelection = selectedLocationId != null;
  useEffect(() => {
    onSelectionChange?.(hasSelection);
  }, [hasSelection, onSelectionChange]);

  const ready = isMapLoaded && imagesInitialized;

  // ── Set up the cluster layers + interactions when the map is ready ──────────
  // The marker set is deliberately NOT a dependency: the clustered source is shared with the
  // mirrored deals, so a teardown on a changed set re-indexes the whole mirror and blanks every
  // mark until it finishes. The set travels through the data effect below instead.
  //
  // The setup/cleanup functions from useClusters/useClusterInteractions are
  // fresh identities on every render (the shared hooks don't memoize them).
  // Listing them as deps would tear down and re-add the Mapbox source + layers
  // on EVERY render (flicker + "layer already exists" races).
  // onSelectLocation/onClearLocation/selectedLocationId churn on every URL
  // change; refs avoid re-running this effect for that.
  const onSelectLocationRef = useRef(onSelectLocation);
  onSelectLocationRef.current = onSelectLocation;
  const onClearLocationRef = useRef(onClearLocation);
  onClearLocationRef.current = onClearLocation;
  const selectedLocationIdRef = useRef(selectedLocationId);
  selectedLocationIdRef.current = selectedLocationId;

  // biome-ignore lint/correctness/useExhaustiveDependencies: setup/cleanup are derived from map; their unstable identities would thrash the Mapbox layers every render
  useEffect(() => {
    if (!ready || !map.current) return;

    setupLocationsClusters();
    new MapLayersService(map.current).orderMiddleLayers();
    const cleanupInteractions = setupClusterInteractions((clickedLocationId: string, count = 1) => {
      if (clickedLocationId === selectedLocationIdRef.current) {
        onClearLocationRef.current();
        return;
      }
      onSelectLocationRef.current(clickedLocationId);
      warnIfCoincident(count);
    });

    return () => {
      cleanupInteractions();
      cleanupLocationsClusters();
    };
  }, [ready, map]);

  // Before the marker write. A hidden choice has to be stored first, or the
  // first paint puts every pin on the map and takes it off a frame later.
  // biome-ignore lint/correctness/useExhaustiveDependencies: map is a stable ref
  useEffect(() => {
    if (!ready || !map.current) return;
    new MapLayersService(map.current).setLocationsVisible(filterState.locationsVisible);
  }, [ready, map, filterState.locationsVisible]);

  // ── Feed the marker set into the shared source ──────────────────────────────
  // Declared after the setup effect so the layers exist on the first write.
  // biome-ignore lint/correctness/useExhaustiveDependencies: applyLocations is derived from (map, clusterableLocations), both already deps
  useEffect(() => {
    if (!ready) return;
    applyLocations();
  }, [ready, map, clusterableLocations]);

  // ── Keep the pin icon in sync with the controlled selection ─────────────────
  // biome-ignore lint/correctness/useExhaustiveDependencies: applySelection is derived from (map, locations), both already deps
  useEffect(() => {
    if (!ready) return;
    applySelection(selectedLocationId);
  }, [ready, locations, selectedLocationId]);

  // ── Frame the camera to the workspace's markers, once per workspace id ──────
  // Only while `allowFit`; tracking the fitted id in a ref means panning does
  // not re-fit and a workspace switch re-frames to the new set.
  const fittedWorkspaceIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!ready || !map.current) return;
    if (!locations.length) return;
    // An open card consumes the fit rather than deferring it: `allowFit` flips
    // true the moment the card closes, and a deferred fit would fire then and
    // yank the camera off the zone (closing a card never moves it — see MapCamera).
    if (!allowFit) {
      fittedWorkspaceIdRef.current = workspaceId;
      return;
    }
    if (fittedWorkspaceIdRef.current === workspaceId) return;

    fittedWorkspaceIdRef.current = workspaceId;

    // A single marker has a degenerate bbox — ease to the point instead. Guard
    // the coordinates the same way the multi-marker fitBounds branch does so a
    // malformed marker never feeds NaN into the camera.
    if (locations.length === 1) {
      const only = locations[0];
      const lng = only.coordinates.longitude;
      const lat = only.coordinates.latitude;
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
      map.current.easeTo({
        center: [lng, lat],
        zoom: SINGLE_MARKER_ZOOM,
        duration: cameraDuration(INITIAL_FIT_DURATION_MS),
      });
      return;
    }

    try {
      const features: Feature<Point>[] = locations.map((loc) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [loc.coordinates.longitude, loc.coordinates.latitude],
        },
        properties: { id: loc.id },
      }));
      const fc: FeatureCollection<Point> = { type: 'FeatureCollection', features };
      const [minLng, minLat, maxLng, maxLat] = bbox(fc);
      if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) return;

      map.current.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        {
          padding: { top: 80, bottom: 60, left: 60, right: 60 },
          duration: cameraDuration(INITIAL_FIT_DURATION_MS),
          pitch: keptMapPitch(map.current),
        },
      );
    } catch {
      // Ignore fit errors — the map stays usable at its current camera.
    }
  }, [allowFit, ready, map, locations, workspaceId]);

  // ── Auto-select on single-pin add ────────────────────────────────────────────
  // When MapSurface raises a new non-null `autoSelectId` (a single-pin add
  // just completed), open its detail without a click. Tracked by the last value
  // consumed so a flapping re-render does not re-open a popup the user dismissed.
  const consumedAutoSelectRef = useRef<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: onSelectLocation churns; dedup is keyed on autoSelectId only
  useEffect(() => {
    if (autoSelectId == null) return;
    if (consumedAutoSelectRef.current === autoSelectId) return;
    consumedAutoSelectRef.current = autoSelectId;
    onSelectLocation(autoSelectId);

    // Warn if the freshly-added pin landed on an existing location's coordinates.
    const added = locations.find((l) => l.id === autoSelectId);
    const count = added
      ? locations.filter(
          (l) =>
            l.coordinates.longitude === added.coordinates.longitude &&
            l.coordinates.latitude === added.coordinates.latitude,
        ).length
      : 1;
    warnIfCoincident(count);
  }, [autoSelectId, locations]);

  // ── Selection + auto-select dedup reset on workspace switch ─────────────────
  // The component is not remounted on switch (MapSurface keeps it mounted and
  // only changes `workspaceId`), so an explicit reset is required or the popup
  // for a location from the previous workspace would survive the switch.
  // The consumedAutoSelectRef is also reset so a re-add on a re-activated
  // workspace can auto-select again.
  const selectedWorkspaceRef = useRef<string>(workspaceId);
  // biome-ignore lint/correctness/useExhaustiveDependencies: onClearLocation deps are unstable; dedup is keyed on workspaceId only
  useEffect(() => {
    if (selectedWorkspaceRef.current === workspaceId) return;
    selectedWorkspaceRef.current = workspaceId;
    onClearLocation();
    consumedAutoSelectRef.current = null;
  }, [workspaceId]);

  // ── On-demand location detail ────────────────────────────────────────────────
  // The selected id drives the results query; it is disabled until a pin is
  // clicked, and again while that location's own compute run is still active
  // (WorkspaceLocationPopup shows its skeleton for that window instead).
  const isSelectedLocationComputing =
    selectedLocationId != null && computingLocationIds.has(selectedLocationId);
  const { data: selectedResults, isError: isResultsError } = useLocationResultsQuery(
    selectedLocationId ?? undefined,
    { enabled: !isSelectedLocationComputing },
  );

  // On a results-fetch error, toast once (keyed by id) and clear the selection
  // so re-clicking the same pin retries, matching the "try again" copy.
  //
  // Re-checking isSelectedLocationComputing here (not just relying on the
  // query's `enabled` gate above) closes a narrow async gap: the fetch that
  // just errored was issued while NOT computing, but the compute-run state
  // (activeComputeRuns, rehydrated from localStorage on a fresh mount — see
  // ManualPinContext) could have caught up by the time this effect runs.
  // Treat that as "still resolving" rather than a hard failure — no toast,
  // no clear, just keep waiting for reconciliation.
  const toastedDetailErrorIdRef = useRef<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: onClearLocation intentionally omitted (keyed on selectedLocationId dedup)
  useEffect(() => {
    if (!isResultsError || !selectedLocationId) {
      if (!selectedLocationId) toastedDetailErrorIdRef.current = null;
      return;
    }
    if (isSelectedLocationComputing) return;
    if (toastedDetailErrorIdRef.current === selectedLocationId) return;
    toastedDetailErrorIdRef.current = selectedLocationId;
    toast.error(LOCATION_DETAIL_FETCH_ERROR_MESSAGE);
    onClearLocation();
  }, [isResultsError, selectedLocationId, isSelectedLocationComputing]);

  // The selected clusterable location (anchor + identity), derived from the
  // lightweight marker set. Its radius is 0 (markers carry no radius), so it is
  // only used for the popup anchor — NOT for the detail layers (see below).
  const selectedMarkerLocation = useMemo<ILocation | undefined>(() => {
    if (!selectedLocationId) return undefined;
    return locations.find((loc) => loc.id === selectedLocationId);
  }, [selectedLocationId, locations]);

  // ── Ease the camera to a newly-selected pin ──────────────────────────────────
  const easedLocationIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedMarkerLocation) {
      easedLocationIdRef.current = null;
      return;
    }
    if (!ready || !map.current) return;
    if (easedLocationIdRef.current === selectedMarkerLocation.id) return;
    easedLocationIdRef.current = selectedMarkerLocation.id;

    map.current.easeTo({
      center: [
        selectedMarkerLocation.coordinates.longitude,
        selectedMarkerLocation.coordinates.latitude,
      ],
      zoom: radiusToZoomLevel(selectedMarkerLocation.radius ?? 0),
    });
  }, [ready, map, selectedMarkerLocation]);

  // The full location fed to the detail layers MUST carry the REAL radius (and
  // address) so the dashed radius circle matches the project map.
  // The marker-derived location hard-codes radius 0, which would
  // draw a degenerate circle — so prefer `selectedResults.location` (the full
  // ILocation fetched for the popup) once it has loaded, falling back to the
  // marker location only as the anchor before results arrive.
  //
  // While the location's own compute run is still active, `selectedLocationId`
  // is a client-generated tempId with no backend record yet — undefined here
  // so `useLocationDetailLayers` stays disabled instead of firing its
  // geospatial queries against an id the backend has never heard of.
  const detailLocation = isSelectedLocationComputing
    ? undefined
    : (selectedResults?.location ?? selectedMarkerLocation);

  // The stored place rides on the results for this location. A previous pin's
  // cached body must not draw a line to the pin just opened.
  const attachedPlace =
    selectedResults != null && selectedResults.location.id === detailLocation?.id
      ? (selectedResults.point_of_interest ?? null)
      : null;
  useLinkedPlace(map, detailLocation?.coordinates, attachedPlace);

  // Apply the SHARED per-location geospatial overlays for the selected pin
  // (same hook the project map uses — reuse, not fork).
  const { layersService, capturedAadtData } = useLocationDetailLayers({
    map,
    isMapLoaded,
    imagesInitialized,
    currentLocation: detailLocation,
  });

  useHighestPerformingCompetitorMark({
    map,
    enabled: ready,
    competitor: selectedResults?.highest_performing_competitor,
    tilesEnabled: competitorTilesEnabled,
    stationFilters: {
      station: filterState.station,
      isElectraVisible: filterState.isElectraVisible,
      isLowHpcVisible: filterState.isLowHpcVisible,
    },
  });

  // ── Map-only: re-apply the retained filter state to each pin's layers ─────
  // Keyed on [layersService, filterState] so the retained filter
  // state re-applies to each newly-selected pin's freshly-built detail layers
  // and on any filter toggle.
  //
  // `layersService` is undefined until a pin is selected and its geospatial
  // queries settle, so the guard is mandatory.
  useEffect(() => {
    if (!layersService) return;
    applyMaxAadtVisibility(layersService, filterState.traffic);
  }, [layersService, filterState]);

  // ── Analytics: emit a project-agnostic inspection event once per opened ─────
  // location. The map surface carries NO project identity, so the payload
  // MUST NOT include a project_id. Routed through useMixpanel (never the SDK
  // directly).
  const trackAction = useMixpanel((s) => s.trackAction);
  const inspectedLocationRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedResults) return;
    const inspectedId = selectedResults.location.id;
    if (inspectedLocationRef.current === inspectedId) return;
    inspectedLocationRef.current = inspectedId;
    trackAction(ACTION_TRACKED.LOCATION_INSPECTED, { location_id: inspectedId });
  }, [selectedResults, trackAction]);

  // Reset the analytics guard when the selection clears so reopening the same
  // location later re-emits the event.
  useEffect(() => {
    if (selectedLocationId == null) inspectedLocationRef.current = null;
  }, [selectedLocationId]);

  // ── Corner detail card ───────────────────────────────────────────────────────
  // Mounts as soon as a pin is selected (on `selectedMarkerLocation` alone) so the
  // click gets immediate feedback: the card shows a loading spinner until the
  // results land, then swaps in the SHARED MarkerMetricsBody (project-map parity)
  // pinned under the search column. On a results-fetch error the card
  // does NOT open (a toast was surfaced above) — the map stays usable.
  const popup =
    map.current && selectedMarkerLocation && !isResultsError ? (
      <WorkspaceLocationPopup
        map={map.current}
        locationData={selectedResults}
        layersService={layersService}
        capturedAadtData={capturedAadtData}
        workspaceId={workspaceId}
        locationId={selectedMarkerLocation.id}
        dialogContainer={shell.containerRef.current ?? undefined}
        onSelectLocation={onSelectLocation}
        onClose={onClearLocation}
        onDealCreated={onDealCreated ?? onSelectDeal}
        computingProgress={isSelectedLocationComputing ? computingProgress : null}
        computingProjectId={isSelectedLocationComputing ? computingProjectId : null}
        onCancelComputing={onCancelComputing}
      />
    ) : null;

  return popup;
}
